import { ElectricityContract, PutUtilityContract } from "@/api/facilities";
import RootModal from "@/components/RootModal/RootModal";
import useAnalytics from "@/hooks/useAnalytics";
import {
  useGetElectricityOptions,
  useGetFacilityProductMass,
  useInsertContract,
  useUpdateContract,
} from "@/hooks/useFacilities";
import useTranslation from "@/hooks/useTranslation";
import usePublishedId from "@/pages/facilities/hooks/usePublishedId";
import { Checkbox, DatePicker, Form, message, Radio, Spin } from "antd";
import { useForm, useWatch } from "antd/es/form/Form";
import dayjs from "dayjs";
import _ from "lodash";
import { useEffect } from "react";
import EstimationSwitch from "../EstimationSwitch";
import styles from "./../Modal.module.scss";
import ElectricitySource from "./ElectricitySource";

const { RangePicker } = DatePicker;

export interface FormSubSource {
  urn: string;
  amount: number;
  percentage: number;
  unit: string;
  parent: string;
  voltage: string;
  capital_owned: boolean;
}

export interface ElectricityContractForm {
  dateRange: [dayjs.Dayjs, dayjs.Dayjs];
  estimation: boolean;
  unit: string;
  type: "green" | "mix" | "grey";
  sources: string[];
  voltage: "low" | "medium" | "high";
  consumption: number;
  subSources: FormSubSource[];
}

export default function ElectricityContractModal({
  facilityId,
  open,
  closeModal,
  editContract: contract,
  minDate,
  maxDate,
}: {
  facilityId: number;
  open: boolean;
  closeModal: () => void;
  editContract?: ElectricityContract;
  minDate: dayjs.Dayjs;
  maxDate: dayjs.Dayjs;
}) {
  const publishedId = usePublishedId();
  const [form] = useForm<ElectricityContractForm>();
  const type = useWatch("type", form);
  const subSources = useWatch("subSources", form);
  const estimation = useWatch("estimation", form);
  const insertContract = useInsertContract();
  const updateContract = useUpdateContract();
  const analytics = useAnalytics({ enabled: !publishedId });
  const translation = useTranslation();

  const { data, isLoading, isError } = useGetElectricityOptions({
    facilityId,
    enabled: open,
  });

  const dateRange = useWatch("dateRange", form);
  const { data: productMass } = useGetFacilityProductMass({
    facilityId,
    startDate: dateRange ? dateRange[0] : minDate,
    endDate: dateRange ? dateRange[1] : maxDate,
  });

  const handleElectricityTypeChange = (newSources: string[]) => {
    const newSubSources = newSources.filter((x) => !subSources.map((y) => y.parent).includes(x));
    const oldSubSources = subSources.filter((x) => newSources.includes(x.parent));
    form.setFieldsValue({
      subSources: [
        ...oldSubSources,
        ...newSubSources.map((x) => {
          return { parent: x, urn: x, voltage: "low", percentage: 100, capital_owned: false };
        }),
      ],
    });
  };

  const onSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        const contractToPutPost: PutUtilityContract = {
          facility_id: facilityId,
          start_date: values.dateRange[0].startOf("month").format("YYYY-MM-DD"),
          end_date: values.dateRange[1].endOf("month").format("YYYY-MM-DD"),
          type: "electricity",
          sources: values.subSources.map((source) => ({
            ...source,
            percentage: values.estimation ? 1 : source.percentage / 100,
            urn: source.urn + ":" + source.voltage.toUpperCase(),
            unit: "kWh",
          })),
          contract_type: values.estimation ? "estimate" : "actual",
        };

        if (contract) {
          updateContract
            .mutateAsync({
              contractId: contract.id,
              contract: { id: contract.id, ...contractToPutPost },
            })
            .then((result) => {
              if (result.status === 200) {
                message.success("Electricity contract updated.");
                analytics.track("Contract Updated");

                closeModal();
              }
            })
            .catch((error) => message.error(translation.errors.general));
        } else {
          insertContract
            .mutateAsync({
              contract: contractToPutPost,
            })
            .then((result) => {
              if (result.status === 200) {
                analytics.track("Contract Added");
                message.success("Electricity contract added.");
                closeModal();
              }
            })
            .catch((error) => message.error(translation.errors.general));
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  useEffect(() => {
    const voltage = contract?.sources.every((source) => source.voltage === "high")
      ? "high"
      : contract?.sources.every((source) => source.voltage === "medium")
        ? "medium"
        : "low";
    form.setFieldsValue({
      dateRange: contract
        ? [dayjs(contract?.start_date), dayjs(contract?.end_date)]
        : minDate && maxDate
          ? [minDate, maxDate]
          : undefined,
      type: contract?.sources.every((source) => source.type === "green")
        ? "green"
        : contract?.sources.every((source) => source.type === "grey")
          ? "grey"
          : "mix",
      voltage: voltage,
      sources: contract?.sources.map((x) => x.parent) || [],
      subSources: contract?.sources.map((x) => ({ ...x, percentage: x.percentage * 100 })) || [],
      estimation: contract?.contract_type === "estimate",
    });
  }, [contract]);

  return (
    <RootModal
      className={styles.ContractModal}
      open={open}
      width={1000}
      onCancel={() => closeModal()}
      okText={"Save"}
      okButtonProps={{
        loading: insertContract.isLoading || updateContract.isLoading,
      }}
      onOk={onSubmit}
      title={"Add electricity contract"}
    >
      <Form<ElectricityContractForm>
        form={form}
        onKeyUp={(e) => {
          if (e.code === "Enter") onSubmit();
        }}
        initialValues={{
          estimation: false,
          dateRange: [minDate, maxDate],
          type: "green",
          voltage: "low",
          sources: [],
          subSources: [],
        }}
      >
        <div className={styles.steps}>
          <div className={styles.step}>
            <div className={styles["step-heading"]}>1. Select timeframe</div>
            <div className={styles["step-content"]}>
              <div>
                <Form.Item
                  name="dateRange"
                  rules={[
                    {
                      required: true,
                      message: "Date range is required.",
                    },
                  ]}
                >
                  <RangePicker picker="month" />
                </Form.Item>
              </div>
            </div>
          </div>
          <div className={styles["step"]}>
            <div className={styles["step-heading"]}>2. What electricity sources do you have?</div>
            <div className={styles["step-subtext"]}>
              This information can be requested from your Electricity supplier.
            </div>
            <div className={styles["step-content"]}>
              <Form.Item
                name="type"
                noStyle={true}
                rules={[
                  {
                    required: true,
                    message: "Type is required.",
                  },
                ]}
              >
                <Radio.Group
                  optionType="button"
                  options={[
                    { label: "Green", value: "green" },
                    { label: "Grey", value: "grey" },
                    { label: "Mix", value: "mix" },
                  ]}
                />
              </Form.Item>
            </div>
            {isLoading ? (
              <div className={styles["step-content"]}>
                <div>
                  <Spin size="small" /> We're collecting the available energy sources for the
                  location of this facility.
                </div>
              </div>
            ) : isError ? (
              <div className={styles.error}>
                The address of this facility does not result in any energy sources.
              </div>
            ) : (
              <>
                <div className={styles["step-subtext"]}>
                  Choose the average mix, if you are unsure.
                </div>
                <Form.Item
                  name="sources"
                  rules={[
                    {
                      required: true,
                      message: "You have to select at least 1 source.",
                    },
                  ]}
                >
                  <Checkbox.Group
                    onChange={handleElectricityTypeChange}
                    options={
                      type &&
                      data?.[type]
                        .filter((option) => !option.parent)
                        .map((option) => ({
                          label: _.upperFirst(option.label),
                          value: option.urn,
                        }))
                        .sort((a, b) => {
                          const score = (value: string) => {
                            if (value.includes("Average mix")) return 1;
                            if (value.includes("mix")) return 0.5;
                            else return 0;
                          };
                          if (score(a.label) !== score(b.label))
                            return score(b.label) - score(a.label);
                          return b.label < a.label ? 1 : -1;
                        })
                    }
                  />
                </Form.Item>
              </>
            )}
          </div>
          <div
            className={styles["step"]}
            style={{ display: subSources?.length > 0 ? "block" : "none" }}
          >
            <div className={styles["step-heading"]}>
              3. Define electricity consumption per source
            </div>
            <div className={styles["step-content"]}>
              <div className={styles.sources}>
                <div className={styles.sourcesWrapper}>
                  <Form.List name="subSources">
                    {(fields, { add, remove }, { errors }) => (
                      <>
                        {fields.map((field) => (
                          <ElectricitySource
                            datasets={data?.datasets}
                            options={data?.mix}
                            key={field.key}
                            form={form}
                            field={field}
                            estimation={estimation}
                            productMass={productMass?.product_mass}
                            facilityId={facilityId}
                          />
                        ))}
                      </>
                    )}
                  </Form.List>
                </div>
                <EstimationSwitch />
              </div>
            </div>
          </div>
        </div>
      </Form>
    </RootModal>
  );
}
